/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import VirtualRow from './VirtualRow';
import {
  useAddingInitialRows,
  useFormSelector,
  useGridTitle,
  useInitializingGridFromDatasource,
} from '../../../../custom-hooks';
import {
  applyFormLevelAddRowRule,
  applyFormLevelClearTableRule,
} from '../../../actions/runtime.actions';
import { FORM_MODEL_ATTRIBUTES } from '../../../constants/form-manager.constants';
import {
  getEvaluatedConditionsForInteractionAction,
  getIsParentlessTable,
} from '../../../selectors/form-manager.selectors';
import {
  getEvaluatedTableRowsProperties,
  getGridColumns,
  getHiddenColumnsMap,
  getSubGridByGridId,
  getVirtualRows,
} from '../../../selectors/ssGrid.selectors';
import { equalityFn } from '../../../utils/form-manager.utils';
import { getTableRowsEqualityFn } from '../../../utils/runtime.utils';

const SSVirtualGrid = ({
  id,
  index,
  entityName,
  childrenNodes,
  isHidden,
  isViewOnly,
  initialRows,
  formKey,
  containersProps,
  addRowRules: { logInfo: addRuleLogInfo } = {},
  clearTableRules: { logInfo: clearTableLogInfo } = {},
  rebindCanvasRules: { logInfo: rebindCanvasLogInfo } = {},
}) => {
  const isParentlessTable = useFormSelector(getIsParentlessTable, { id });
  const selectorProps = { id, index, entityName, isParentlessTable };
  const [isRebindCanvas, setIsRebindCanvas] = useState(false);

  const dispatch = useDispatch();
  const rows = useFormSelector(
    getVirtualRows,
    selectorProps,
    getTableRowsEqualityFn(isParentlessTable)
  );
  const columns = useFormSelector(getGridColumns, { childrenNodes }, equalityFn);
  const title = useGridTitle(selectorProps);
  const shouldAddDefaultRow = useFormSelector(getEvaluatedConditionsForInteractionAction, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.ADD_ROW_RULES,
  });
  const shouldClearTable = useFormSelector(getEvaluatedConditionsForInteractionAction, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.CLEAR_TABLE_RULES,
  });
  const shouldRebindCanvas = useFormSelector(getEvaluatedConditionsForInteractionAction, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.REBIND_CANVAS_RULES,
  });
  const disableEditFilter = useFormSelector(
    getEvaluatedTableRowsProperties,
    {
      ...selectorProps,
      prop: FORM_MODEL_ATTRIBUTES.DISABLE_EDIT,
    },
    equalityFn
  );
  const hiddenColumnsMap = useFormSelector(
    getHiddenColumnsMap,
    { childrenNodes, index, entityName },
    equalityFn
  );
  const subgridId = useFormSelector(getSubGridByGridId, { id });

  const rowIndexKey = isParentlessTable ? 'gridArrIndex' : 'index';
  const rowsLength = rows.filter((row) => !row.isFilteredOut).length;
  const commonInteractionRulesArgs = { index, entityName, formKey };

  useAddingInitialRows({
    initialRows,
    selectorProps,
    isVirtualComponent: true,
    isRebindCanvas,
    rebindCanvasLogInfo,
  });

  useInitializingGridFromDatasource({
    rowsLength,
    selectorProps,
    isVirtualComponent: true,
    isRebindCanvas,
    rebindCanvasLogInfo,
  });

  useEffect(() => {
    if (shouldAddDefaultRow) {
      dispatch(applyFormLevelAddRowRule({ addRuleLogInfo, ...commonInteractionRulesArgs }));
    }
  }, [shouldAddDefaultRow]);

  useEffect(() => {
    if (shouldClearTable) {
      dispatch(
        applyFormLevelClearTableRule({ clearTableLogInfo, rows, ...commonInteractionRulesArgs })
      );
    }
  }, [shouldClearTable]);

  useEffect(() => {
    if (shouldRebindCanvas) {
      setIsRebindCanvas(true);
    }
  }, [shouldRebindCanvas]);

  useEffect(() => {
    if (isRebindCanvas) {
      setIsRebindCanvas(false);
    }
  }, [isRebindCanvas]);

  return rows.map((row, i) => (
    <VirtualRow
      id={id}
      key={row.key}
      columns={columns}
      hiddenColumnsMap={hiddenColumnsMap}
      rowIndex={row[rowIndexKey]}
      index={index}
      isHidden={isHidden || row.isFilteredOut}
      isViewOnly={isViewOnly || disableEditFilter[row[rowIndexKey]]}
      subgridId={subgridId}
      containersProps={containersProps}
      gridLabel={title}
      isParentlessTable={isParentlessTable}
      gridIndex={i}
    />
  ));
};

export default React.memo(SSVirtualGrid);
